import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Grid,
  Box,
  CircularProgress,
  Alert,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  TextField,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Search as SearchIcon,
  Today as TodayIcon,
  School as SchoolIcon,
  Person as StudentIcon,
  Group as TeacherIcon,
  CalendarToday as DateIcon
} from '@mui/icons-material';

interface StudentRecord {
  student__student_id: string;
  student__first_name: string;
  student__last_name: string;
  school__name: string;
  school__district__name: string;
  class_name: string;
  status: string;
  arrival_time: string;
  created_at: string;
}

interface TeacherRecord {
  teacher__emp_no: string;
  teacher__first_name: string;
  teacher__last_name: string;
  school__name: string;
  school__district__name: string;
  status: string;
  arrival_time: string;
  departure_time: string;
  created_at: string;
}

interface SchoolSummary {
  school__name: string;
  school__district__name: string;
  school__emis_id: string;
  total_records: number;
  present_count: number;
  absent_count: number;
  late_count?: number;
  on_leave_count?: number;
  students_count?: number;
  teachers_count?: number;
}

interface DateSummary {
  attendance_date: string;
  schools_count: number;
  students_count?: number;
  teachers_count?: number;
  records_count: number;
}

interface TodayData {
  target_date: string;
  student_attendance_summary: {
    total_schools_with_records: number;
    total_student_records: number;
    unique_students: number;
    schools_detail: SchoolSummary[];
  };
  teacher_attendance_summary: {
    total_schools_with_records: number;
    total_teacher_records: number;
    unique_teachers: number;
    schools_detail: SchoolSummary[];
  };
  recent_dates_with_data: {
    student_attendance_dates: DateSummary[];
    teacher_attendance_dates: DateSummary[];
  };
  sample_records: {
    student_records: StudentRecord[];
    teacher_records: TeacherRecord[];
  };
}

const AttendanceDebugView: React.FC = () => {
  const [todayData, setTodayData] = useState<TodayData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);

  useEffect(() => {
    fetchTodayData();
  }, [selectedDate]);

  const fetchTodayData = async () => {
    try {
      setLoading(true);
      setError(null);

      const { attendanceAPI } = await import('../../services/attendanceApi');
      const response = await attendanceAPI.getTodayDebug({
        date: selectedDate,
      });

      if (response.data.status === 'success') {
        setTodayData(response.data.data);
      } else {
        throw new Error(response.data.message || 'Failed to load attendance debug data');
      }
    } catch (err: any) {
      console.error('Attendance debug error:', err);
      setError(err.response?.data?.message || err.message || 'Failed to load attendance debug data');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string): 'success' | 'warning' | 'error' | 'info' => {
    switch (status.toLowerCase()) {
      case 'present':
        return 'success';
      case 'late':
        return 'warning';
      case 'absent':
        return 'error';
      default:
        return 'info';
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }

  if (!todayData) {
    return (
      <Alert severity="info" sx={{ mb: 2 }}>
        No attendance debug data available
      </Alert>
    );
  }

  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        <SearchIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
        Attendance Records Debug View
      </Typography>

      <Typography variant="body2" color="textSecondary" gutterBottom>
        View actual attendance records to verify data quality and completeness
      </Typography>

      {/* Date Selector */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Select Date"
                type="date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                size="small"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <Button
                variant="contained"
                onClick={fetchTodayData}
                startIcon={<SearchIcon />}
                fullWidth
              >
                Load Attendance Data
              </Button>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box display="flex" alignItems="center">
                <TodayIcon sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6">
                  {todayData.target_date}
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom color="primary">
                <StudentIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
                Student Attendance
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="body2" color="textSecondary">Schools with Records</Typography>
                  <Typography variant="h4">{todayData.student_attendance_summary.total_schools_with_records}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="textSecondary">Total Records</Typography>
                  <Typography variant="h4">{todayData.student_attendance_summary.total_student_records}</Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="body2" color="textSecondary">Unique Students</Typography>
                  <Typography variant="h5">{todayData.student_attendance_summary.unique_students}</Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom color="primary">
                <TeacherIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
                Teacher Attendance
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="body2" color="textSecondary">Schools with Records</Typography>
                  <Typography variant="h4">{todayData.teacher_attendance_summary.total_schools_with_records}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="textSecondary">Total Records</Typography>
                  <Typography variant="h4">{todayData.teacher_attendance_summary.total_teacher_records}</Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="body2" color="textSecondary">Unique Teachers</Typography>
                  <Typography variant="h5">{todayData.teacher_attendance_summary.unique_teachers}</Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Schools with Attendance */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Schools with Student Attendance
              </Typography>
              {todayData.student_attendance_summary.schools_detail.length > 0 ? (
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>School</TableCell>
                        <TableCell>District</TableCell>
                        <TableCell align="right">Students</TableCell>
                        <TableCell align="right">Present</TableCell>
                        <TableCell align="right">Absent</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {todayData.student_attendance_summary.schools_detail.map((school, index) => (
                        <TableRow key={index} hover>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                              {school.school__name.trim()}
                            </Typography>
                            <Typography variant="caption" color="textSecondary">
                              {school.school__emis_id}
                            </Typography>
                          </TableCell>
                          <TableCell>{school.school__district__name}</TableCell>
                          <TableCell align="right">{school.students_count}</TableCell>
                          <TableCell align="right">
                            <Chip label={school.present_count} color="success" size="small" />
                          </TableCell>
                          <TableCell align="right">
                            <Chip label={school.absent_count} color="error" size="small" />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Alert severity="warning">No schools have submitted student attendance for this date</Alert>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Schools with Teacher Attendance
              </Typography>
              {todayData.teacher_attendance_summary.schools_detail.length > 0 ? (
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>School</TableCell>
                        <TableCell>District</TableCell>
                        <TableCell align="right">Teachers</TableCell>
                        <TableCell align="right">Present</TableCell>
                        <TableCell align="right">Absent</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {todayData.teacher_attendance_summary.schools_detail.map((school, index) => (
                        <TableRow key={index} hover>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                              {school.school__name.trim()}
                            </Typography>
                            <Typography variant="caption" color="textSecondary">
                              {school.school__emis_id}
                            </Typography>
                          </TableCell>
                          <TableCell>{school.school__district__name}</TableCell>
                          <TableCell align="right">{school.teachers_count}</TableCell>
                          <TableCell align="right">
                            <Chip label={school.present_count} color="success" size="small" />
                          </TableCell>
                          <TableCell align="right">
                            <Chip label={school.absent_count} color="error" size="small" />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Alert severity="warning">No schools have submitted teacher attendance for this date</Alert>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Recent Dates with Data */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            <DateIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
            Recent Dates with Attendance Data
          </Typography>

          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle1" gutterBottom>Student Attendance Dates</Typography>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Date</TableCell>
                      <TableCell align="right">Schools</TableCell>
                      <TableCell align="right">Students</TableCell>
                      <TableCell align="right">Records</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {todayData.recent_dates_with_data.student_attendance_dates.slice(0, 5).map((dateData, index) => (
                      <TableRow key={index} hover>
                        <TableCell>{dateData.attendance_date}</TableCell>
                        <TableCell align="right">{dateData.schools_count}</TableCell>
                        <TableCell align="right">{dateData.students_count}</TableCell>
                        <TableCell align="right">{dateData.records_count}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>

            <Grid item xs={12} md={6}>
              <Typography variant="subtitle1" gutterBottom>Teacher Attendance Dates</Typography>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Date</TableCell>
                      <TableCell align="right">Schools</TableCell>
                      <TableCell align="right">Teachers</TableCell>
                      <TableCell align="right">Records</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {todayData.recent_dates_with_data.teacher_attendance_dates.slice(0, 5).map((dateData, index) => (
                      <TableRow key={index} hover>
                        <TableCell>{dateData.attendance_date}</TableCell>
                        <TableCell align="right">{dateData.schools_count}</TableCell>
                        <TableCell align="right">{dateData.teachers_count}</TableCell>
                        <TableCell align="right">{dateData.records_count}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Sample Records */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6">View Sample Records</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle1" gutterBottom>Sample Student Records</Typography>
              {todayData.sample_records.student_records.length > 0 ? (
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Student ID</TableCell>
                        <TableCell>Name</TableCell>
                        <TableCell>Class</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Time</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {todayData.sample_records.student_records.map((record, index) => (
                        <TableRow key={index} hover>
                          <TableCell>{record.student__student_id}</TableCell>
                          <TableCell>{record.student__first_name} {record.student__last_name}</TableCell>
                          <TableCell>{record.class_name}</TableCell>
                          <TableCell>
                            <Chip
                              label={record.status}
                              color={getStatusColor(record.status)}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>{record.arrival_time}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Alert severity="info">No student records for this date</Alert>
              )}
            </Grid>

            <Grid item xs={12} md={6}>
              <Typography variant="subtitle1" gutterBottom>Sample Teacher Records</Typography>
              {todayData.sample_records.teacher_records.length > 0 ? (
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Employee No</TableCell>
                        <TableCell>Name</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Arrival</TableCell>
                        <TableCell>Departure</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {todayData.sample_records.teacher_records.map((record, index) => (
                        <TableRow key={index} hover>
                          <TableCell>{record.teacher__emp_no}</TableCell>
                          <TableCell>{record.teacher__first_name} {record.teacher__last_name}</TableCell>
                          <TableCell>
                            <Chip
                              label={record.status}
                              color={getStatusColor(record.status)}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>{record.arrival_time}</TableCell>
                          <TableCell>{record.departure_time}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Alert severity="info">No teacher records for this date</Alert>
              )}
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>
    </Box>
  );
};

export default AttendanceDebugView;